import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { CityStateSelect } from '../components/ui/CityStateSelect';
import { SpecialitySelect } from '../components/ui/SpecialitySelect';

// ─── Constants ────────────────────────────────────────────────────
const fmt = n => { if(!n||n===0)return'₹0';if(n>=10000000)return`₹${(n/10000000).toFixed(1)}Cr`;if(n>=100000)return`₹${(n/100000).toFixed(1)}L`;if(n>=1000)return`₹${(n/1000).toFixed(0)}K`;return`₹${Math.round(n)}`; };
const PURPLE='#403EC9';
const PRODUCTS=['alveoFlow FOT','alveoair Clinic','alveoMD','alveoMDlite','alveoASHA','6MWT','alveoSafe Viral Filters','Turbine','Disposable Mouthpiece'];
const OUTCOMES=['Positive - will consider','Demo scheduled','Demo done','Order placed','Needs follow-up','Not interested','FOT installed','Consumable reorder done'];
const STAGES=['prospect','contacted','demo_done','evaluating','converted','recurring','partner'];
const STAGE_LABELS={prospect:'Prospect',contacted:'Contacted',demo_done:'Demo Done',evaluating:'Evaluating',converted:'Converted',recurring:'Recurring',partner:'Partner'};
const STAGE_COLOR={prospect:'#6B7280',contacted:'#2563EB',demo_done:'#7C3AED',evaluating:'#D97706',converted:'#16A34A',recurring:'#059669',partner:'#0F766E'};
const VISIT_TYPES=['First Visit','Follow-up','Demo','Re-engagement','KOL Meeting','Distributor Review','CME / Event','Consumable Order'];

// ─── Mobile Shell ─────────────────────────────────────────────────
export default function MobileApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('today');
  const [logVisit, setLogVisit] = useState(false);
  const [viewAccount, setViewAccount] = useState(null);

  const role = user?.role_slug || user?.role || '';

  const tabs = [
    { id:'today',       icon:'📅', label:'Today' },
    { id:'accounts',    icon:'👥', label:'Accounts' },
    { id:'followups',   icon:'🔔', label:'Follow-ups' },
    { id:'conference',  icon:'🎪', label:'Conference' },
    { id:'expenses',    icon:'💰', label:'Expenses' },
    { id:'me',          icon:'📊', label:'My Stats' },
  ];

  return (
    <div style={{ maxWidth:480, margin:'0 auto', height:'100dvh', display:'flex', flexDirection:'column', background:'var(--bg-card)', position:'relative', overflow:'hidden' }}>

      {/* Top bar */}
      <div style={{ padding:'12px 16px 8px', background:PURPLE, color:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
        <div>
          <div style={{ fontSize:'0.72rem', opacity:0.8 }}>alveofit · field</div>
          <div style={{ fontWeight:700, fontSize:'0.95rem' }}>{user?.name}</div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
            <div style={{ fontSize:'0.68rem', background:'rgba(255,255,255,0.2)', padding:'2px 8px', borderRadius:99 }}>{role.replace('_',' ').toUpperCase()}</div>
            <div style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.6)' }}>Field Mode</div>
          </div>
          <button onClick={logout} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:'0.75rem', cursor:'pointer' }}>Exit</button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex:1, overflow:'auto', WebkitOverflowScrolling:'touch' }}>
        {tab==='today'      && <TodayTab      onLogVisit={()=>setLogVisit(true)} onViewAccount={setViewAccount} />}
        {tab==='accounts'   && <AccountsTab   onViewAccount={setViewAccount} onLogVisit={acc=>{setViewAccount(acc);}} />}
        {tab==='followups'  && <FollowupsTab  onLogVisit={()=>setLogVisit(true)} onViewAccount={setViewAccount} />}
        {tab==='conference' && <ConferenceTab />}
        {tab==='expenses'   && <MobileExpensesTab />}
        {tab==='me'         && <MeTab />}
      </div>

      {/* Bottom tab bar */}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${tabs.length},1fr)`, background:'var(--bg-card)', borderTop:'1px solid var(--border)', flexShrink:0, paddingBottom:'env(safe-area-inset-bottom)' }}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:'8px 0', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:2,
              background:tab===t.id?'#EEEDFE':'var(--bg-card)',
              color:tab===t.id?PURPLE:'var(--text-muted)',
              borderTop:tab===t.id?`2px solid ${PURPLE}`:'2px solid transparent' }}>
            <span style={{ fontSize:16 }}>{t.icon}</span>
            <span style={{ fontSize:'0.58rem', fontWeight:tab===t.id?700:400 }}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Log Visit FAB — hide on conference tab */}
      {tab !== 'conference' && (
        <button onClick={()=>setLogVisit(true)}
          style={{ position:'absolute', right:16, bottom:80, width:52, height:52, borderRadius:'50%', background:PURPLE, color:'#fff', border:'none', cursor:'pointer', fontSize:22, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(64,62,201,0.4)', zIndex:10 }}>
          +
        </button>
      )}

      {logVisit   && <LogVisitModal onClose={()=>setLogVisit(false)} onSaved={()=>setLogVisit(false)} />}
      {viewAccount&& <AccountPlaybookModal account={viewAccount} onClose={()=>setViewAccount(null)} onLogVisit={()=>{setViewAccount(null);setLogVisit(true);}} />}
    </div>
  );
}


// ─── CONFERENCE TAB ───────────────────────────────────────────────
function ConferenceTab() {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null); // conference object

  useEffect(() => {
    api.get('/conferences', { params: { status: 'ongoing', limit: 20 } })
      .then(r => setConferences(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (selected) return <MobileConferenceDetail conf={selected} onBack={() => setSelected(null)} />;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Field mode</div>
        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Conference Mode</div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>}

      {!loading && conferences.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🎪</div>
          <div style={{ fontWeight: 600 }}>No active conferences</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Upcoming conferences will appear here
          </div>
        </div>
      )}

      {conferences.map(c => {
        const start = new Date(c.start_date + 'T00:00:00');
        const end   = new Date(c.end_date   + 'T00:00:00');
        const today = new Date();
        const isLive = today >= start && today <= end;
        return (
          <div key={c.id} onClick={() => setSelected(c)}
            style={{ background: 'var(--bg)', borderRadius: 12, marginBottom: 12, border: `1px solid ${isLive ? PURPLE : 'var(--border)'}`, overflow: 'hidden', cursor: 'pointer' }}>
            {isLive && (
              <div style={{ padding: '4px 14px', background: PURPLE, fontSize: '0.68rem', fontWeight: 700, color: '#fff', letterSpacing: '0.05em' }}>
                🔴 LIVE NOW
              </div>
            )}
            <div style={{ padding: '12px 14px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>{c.conference_name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                {c.city}{c.venue_name ? ` · ${c.venue_name}` : ''} · {start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}–{end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: PURPLE }}>{c.total_leads ?? '—'}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Leads</div>
                </div>
                <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16A34A' }}>{c.total_attendees ?? '—'}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Checked-in</div>
                </div>
                <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#D97706' }}>{c.total_activities ?? '—'}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Activities</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── MOBILE CONFERENCE DETAIL ─────────────────────────────────────
function MobileConferenceDetail({ conf, onBack }) {
  const [view, setView]               = useState('home'); // home | leads | activities | checkin
  const [leads, setLeads]             = useState([]);
  const [activities, setActivities]   = useState([]);
  const [loadingLeads, setLLoading]   = useState(false);
  const [loadingActs, setALoading]    = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  const loadLeads = () => {
    setLLoading(true);
    api.get(`/conferences/${conf.id}/leads`)
      .then(r => setLeads(r.data.data || []))
      .catch(() => {})
      .finally(() => setLLoading(false));
  };

  const loadActivities = () => {
    setALoading(true);
    api.get(`/conferences/${conf.id}/activities`)
      .then(r => setActivities(r.data.data || []))
      .catch(() => {})
      .finally(() => setALoading(false));
  };

  useEffect(() => { loadLeads(); loadActivities(); }, [conf.id]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: PURPLE, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 4 }}>‹ All Conferences</button>
        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{conf.conference_name}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{conf.city}{conf.venue_name ? ` · ${conf.venue_name}` : ''}</div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '12px 16px', flexShrink: 0 }}>
        <button onClick={() => setShowCapture(true)}
          style={{ background: PURPLE, color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
          📋 Capture Lead
        </button>
        <button onClick={() => setShowActivity(true)}
          style={{ background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
          📝 Log Activity
        </button>
      </div>

      {/* Sub tabs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {[
          { id: 'leads',      label: `Leads (${leads.length})` },
          { id: 'activities', label: `Activities (${activities.length})` },
          { id: 'checkin',    label: 'Check-in' },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            style={{ padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: view === t.id ? 700 : 400,
              background: 'var(--bg-card)',
              color: view === t.id ? PURPLE : 'var(--text-muted)',
              borderBottom: view === t.id ? `2px solid ${PURPLE}` : '2px solid transparent' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {view === 'leads' && (
          <>
            {loadingLeads && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Loading…</div>}
            {!loadingLeads && leads.length === 0 && (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                No leads yet — tap Capture Lead
              </div>
            )}
            {leads.map(l => (
              <div key={l.id} style={{ background: 'var(--bg)', borderRadius: 10, marginBottom: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{l.lead_name}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {[l.designation, l.speciality, l.hospital_name].filter(Boolean).join(' · ')}
                </div>
                {l.interest_level && (
                  <span style={{ display: 'inline-block', marginTop: 6, fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                    background: l.interest_level === 'hot' ? '#FEE2E2' : l.interest_level === 'warm' ? '#FEF3C7' : '#F3F4F6',
                    color: l.interest_level === 'hot' ? '#DC2626' : l.interest_level === 'warm' ? '#D97706' : '#6B7280' }}>
                    {l.interest_level.toUpperCase()}
                  </span>
                )}
              </div>
            ))}
          </>
        )}

        {view === 'activities' && (
          <>
            {loadingActs && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Loading…</div>}
            {!loadingActs && activities.length === 0 && (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📝</div>
                No activities yet — tap Log Activity
              </div>
            )}
            {activities.map(a => (
              <div key={a.id} style={{ background: 'var(--bg)', borderRadius: 10, marginBottom: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{a.title || a.activity_type}</div>
                  <span style={{ fontSize: '0.68rem', padding: '2px 8px', borderRadius: 99, background: '#EEEDFE', color: PURPLE, fontWeight: 600 }}>{a.activity_type}</span>
                </div>
                {a.speaker_name && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Speaker: {a.speaker_name}</div>}
                {a.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 6 }}>{a.notes}</div>}
              </div>
            ))}
          </>
        )}

        {view === 'checkin' && <MobileConferenceCheckin confId={conf.id} />}
      </div>

      {showCapture && (
        <MobileCaptureLeadSheet confId={conf.id} onClose={() => setShowCapture(false)} onSaved={() => { setShowCapture(false); loadLeads(); }} />
      )}
      {showActivity && (
        <MobileLogActivitySheet confId={conf.id} onClose={() => setShowActivity(false)} onSaved={() => { setShowActivity(false); loadActivities(); }} />
      )}
    </div>
  );
}

// ─── CONFERENCE CHECK-IN ──────────────────────────────────────────
function MobileConferenceCheckin({ confId }) {
  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);
  const [checking, setChecking] = useState(null);
  const [attendees, setAttendees] = useState([]);

  useEffect(() => {
    api.get(`/conferences/${confId}/attendees`)
      .then(r => setAttendees(r.data.data || []))
      .catch(() => {});
  }, [confId]);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get('/doctors', { params: { search, limit: 8 } })
        .then(r => setResults(r.data.data || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const checkIn = async (doctor) => {
    setChecking(doctor.id);
    try {
      await api.post(`/conferences/${confId}/attendees`, { doctor_id: doctor.id });
      toast.success(`${doctor.name} checked in`);
      setAttendees(prev => [...prev, { doctor_id: doctor.id, doctor_name: doctor.name, checked_in_at: new Date().toISOString() }]);
      setSearch(''); setResults([]);
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setChecking(null); }
  };

  const alreadyIn = new Set(attendees.map(a => a.doctor_id));

  return (
    <div>
      <input className="form-control" value={search} autoComplete="off"
        placeholder="Search doctor to check in…"
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 10 }} />
      {results.map(d => (
        <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{d.name}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{d.speciality} · {d.district}</div>
          </div>
          {alreadyIn.has(d.id) ? (
            <span style={{ fontSize: '0.72rem', color: '#16A34A', fontWeight: 600 }}>✓ In</span>
          ) : (
            <button onClick={() => checkIn(d)} disabled={checking === d.id}
              style={{ background: PURPLE, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              {checking === d.id ? '…' : 'Check In'}
            </button>
          )}
        </div>
      ))}
      {attendees.length > 0 && (
        <>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 14, marginBottom: 8 }}>Checked In ({attendees.length})</div>
          {attendees.map(a => (
            <div key={a.doctor_id || a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#DCFCE7', borderRadius: 8, marginBottom: 4 }}>
              <span style={{ color: '#16A34A', fontWeight: 700 }}>✓</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{a.doctor_name}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── CAPTURE LEAD SHEET ───────────────────────────────────────────
function MobileCaptureLeadSheet({ confId, onClose, onSaved }) {
  const INTEREST = ['hot', 'warm', 'cold'];
  const [form, setForm] = useState({
    lead_name: '', designation: '', speciality: '', hospital_name: '',
    city: '', state: '', phone: '', email: '',
    products_interested: [], interest_level: 'warm', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleProd = p => setForm(prev => ({
    ...prev,
    products_interested: prev.products_interested.includes(p)
      ? prev.products_interested.filter(x => x !== p)
      : [...prev.products_interested, p],
  }));

  const save = async () => {
    if (!form.lead_name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post(`/conferences/${confId}/leads`, form);
      toast.success('Lead captured');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ padding: '12px 16px 0', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
          <div style={{ width: 40, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 14px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Capture Lead</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
          </div>
        </div>
        <div style={{ padding: '0 16px 24px' }}>
          <input className="form-control" value={form.lead_name} autoFocus placeholder="Full name *"
            onChange={e => set('lead_name', e.target.value)} style={{ marginBottom: 10 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <input className="form-control" value={form.designation} placeholder="Designation"
              onChange={e => set('designation', e.target.value)} />
            <SpecialitySelect value={form.speciality} onChange={v => set('speciality', v)} label="" placeholder="Speciality" />
          </div>
          <input className="form-control" value={form.hospital_name} placeholder="Hospital / Institution"
            onChange={e => set('hospital_name', e.target.value)} style={{ marginBottom: 10 }} />
          <div style={{ marginBottom: 10 }}>
            <CityStateSelect
              city={form.city} state={form.state}
              onCityChange={v => set('city', v)} onStateChange={v => set('state', v)}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <input className="form-control" value={form.phone} placeholder="Phone" type="tel"
              onChange={e => set('phone', e.target.value)} />
            <input className="form-control" value={form.email} placeholder="Email" type="email"
              onChange={e => set('email', e.target.value)} />
          </div>

          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Products Interested</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {PRODUCTS.map(p => (
              <button key={p} onClick={() => toggleProd(p)}
                style={{ padding: '6px 10px', borderRadius: 7, fontSize: '0.72rem', cursor: 'pointer', border: 'none', fontWeight: 500,
                  background: form.products_interested.includes(p) ? PURPLE : 'var(--bg)',
                  color: form.products_interested.includes(p) ? '#fff' : 'var(--text-secondary)' }}>
                {p}
              </button>
            ))}
          </div>

          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Interest Level</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {INTEREST.map(lvl => (
              <button key={lvl} onClick={() => set('interest_level', lvl)}
                style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                  background: form.interest_level === lvl
                    ? (lvl === 'hot' ? '#DC2626' : lvl === 'warm' ? '#D97706' : '#6B7280')
                    : 'var(--bg)',
                  color: form.interest_level === lvl ? '#fff' : 'var(--text-muted)' }}>
                {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
              </button>
            ))}
          </div>

          <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="Notes…" style={{ marginBottom: 14 }} />

          <button onClick={save} disabled={saving}
            style={{ width: '100%', background: PURPLE, color: '#fff', border: 'none', borderRadius: 10, padding: 13, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save Lead ✓'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LOG ACTIVITY SHEET ───────────────────────────────────────────
function MobileLogActivitySheet({ confId, onClose, onSaved }) {
  const ACT_TYPES = ['Symposium','Workshop','Product Demo','CME Session','Booth Interaction','KOL Interaction','Networking','Panel Discussion'];
  const [form, setForm] = useState({
    activity_type: 'Booth Interaction', title: '', speaker_name: '',
    products_discussed: [], notes: '',
    activity_date: new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleProd = p => setForm(prev => ({
    ...prev,
    products_discussed: prev.products_discussed.includes(p)
      ? prev.products_discussed.filter(x => x !== p)
      : [...prev.products_discussed, p],
  }));

  const save = async () => {
    if (!form.activity_type) return toast.error('Select activity type');
    setSaving(true);
    try {
      await api.post(`/conferences/${confId}/activities`, form);
      toast.success('Activity logged');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '12px 16px 0', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
          <div style={{ width: 40, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 14px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Log Activity</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
          </div>
        </div>
        <div style={{ padding: '0 16px 24px' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Activity Type</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {ACT_TYPES.map(t => (
              <button key={t} onClick={() => set('activity_type', t)}
                style={{ padding: '7px 11px', borderRadius: 7, fontSize: '0.75rem', cursor: 'pointer', border: 'none', fontWeight: 500,
                  background: form.activity_type === t ? '#7C3AED' : 'var(--bg)',
                  color: form.activity_type === t ? '#fff' : 'var(--text-secondary)' }}>
                {t}
              </button>
            ))}
          </div>
          <input className="form-control" value={form.title} placeholder="Title / Session name"
            onChange={e => set('title', e.target.value)} style={{ marginBottom: 10 }} />
          <input className="form-control" value={form.speaker_name} placeholder="Speaker name (optional)"
            onChange={e => set('speaker_name', e.target.value)} style={{ marginBottom: 10 }} />
          <input type="date" className="form-control" value={form.activity_date}
            onChange={e => set('activity_date', e.target.value)} style={{ marginBottom: 10 }} />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Products Discussed</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {PRODUCTS.map(p => (
              <button key={p} onClick={() => toggleProd(p)}
                style={{ padding: '6px 10px', borderRadius: 7, fontSize: '0.72rem', cursor: 'pointer', border: 'none', fontWeight: 500,
                  background: form.products_discussed.includes(p) ? '#7C3AED' : 'var(--bg)',
                  color: form.products_discussed.includes(p) ? '#fff' : 'var(--text-secondary)' }}>
                {p}
              </button>
            ))}
          </div>
          <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="Notes…" style={{ marginBottom: 14 }} />
          <button onClick={save} disabled={saving}
            style={{ width: '100%', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 10, padding: 13, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save Activity ✓'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Mobile Recommendations ───────────────────────────────────────
function MobileRecommendations({ recs, onLogVisit }) {
  const scoreColor = (score) => score >= 80 ? '#DC2626' : score >= 55 ? '#D97706' : '#16A34A';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Visit these today</div>
      {recs.map((doc) => (
        <div key={doc.id} style={{ background: 'var(--bg-card)', borderRadius: 12, marginBottom: 8, border: '1px solid var(--border)', borderLeft: '3px solid ' + scoreColor(doc.score), overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{doc.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 1 }}>{doc.district}{doc.focus_product_1 ? ' · ' + doc.focus_product_1 : ''}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: scoreColor(doc.score), lineHeight: 1 }}>{doc.score}</div>
                <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>score</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: doc.priority === 'A' ? '#EEEDFE' : '#F1EFE8', color: doc.priority === 'A' ? PURPLE : '#5F5E5A' }}>Priority {doc.priority}</span>
              {doc.overdue_follow_up && <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: '#FEE2E2', color: '#DC2626' }}>Follow-up overdue</span>}
            </div>
            {doc.reasons[0] && <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: doc.talking_point ? 4 : 6 }}>{doc.reasons.join(' · ')}</div>}
            {doc.talking_point && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>Pitch: {doc.talking_point}</div>}
            <button onClick={onLogVisit} style={{ width: '100%', background: PURPLE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>+ Log Visit</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── TODAY TAB ────────────────────────────────────────────────────
function TodayTab({ onLogVisit, onViewAccount }) {
  const [plan, setPlan]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    api.get('/dashboard/recommendations')
      .then(r => setRecs(r.data.data?.recommendations || []))
      .catch(() => {});
    const today = new Date().toISOString().split('T')[0];
    api.get('/travel-plan', { params:{ from_date:today, to_date:today, limit:50 } })
      .then(r => setPlan(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const ACT_COLOR = {
    'Field beat':'#2563EB','KOL / institutional day':'#7C3AED',
    'Follow-up / consumable day':'#16A34A','Distributor review':'#E11D48',
    'Monthly review / pipeline':'#D97706','Event / CME':'#9333EA',
    'Hiring / onboarding':'#EA580C','Weekly Off':'#9CA3AF',
  };

  const today = new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'});
  const activities = plan.filter(p=>p.activity_type!=='Weekly Off');
  const isOff      = plan.some(p=>p.activity_type==='Weekly Off') && activities.length===0;

  return (
    <div style={{ padding:16 }}>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Today</div>
        <div style={{ fontWeight:700, fontSize:'1.1rem' }}>{today}</div>
      </div>

      {!loading && recs.length > 0 && <MobileRecommendations recs={recs} onLogVisit={onLogVisit} />}

      {loading && <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading…</div>}

      {!loading && isOff && (
        <div style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🏖️</div>
          <div style={{ fontWeight:600 }}>Weekly Off</div>
          <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginTop:4 }}>Rest up — big week ahead</div>
        </div>
      )}

      {!loading && !isOff && activities.length===0 && (
        <div style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>📅</div>
          <div style={{ fontWeight:600 }}>No plan for today</div>
          <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginTop:4 }}>Check with your RSM</div>
        </div>
      )}

      {activities.map((act, i) => {
        const color = ACT_COLOR[act.activity_type] || '#6B7280';
        const accounts = act.focus_accounts ? act.focus_accounts.split('/').map(a=>a.trim()).filter(Boolean) : [];
        const products = act.product_focus ? act.product_focus.split('+').map(p=>p.trim()).filter(Boolean) : [];
        return (
          <div key={i} style={{ background:'var(--bg)', borderRadius:12, marginBottom:12, overflow:'hidden', border:'1px solid var(--border)' }}>
            {/* Activity header */}
            <div style={{ background:color, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ color:'#fff', fontWeight:700, fontSize:'0.88rem' }}>{act.activity_type}</div>
              <div style={{ color:'rgba(255,255,255,0.85)', fontSize:'0.72rem' }}>{act.planned_calls||0} calls · {Math.round(act.planned_demos||0)} demos</div>
            </div>
            <div style={{ padding:'12px 14px' }}>
              {act.city_cluster && <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginBottom:8 }}>📍 {act.city_cluster}</div>}

              {/* Focus accounts */}
              {accounts.length>0 && (
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Focus Accounts</div>
                  {accounts.slice(0,4).map((a,j) => (
                    <div key={j} onClick={()=>onViewAccount({name:a})}
                      style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:'var(--bg-card)', borderRadius:7, marginBottom:4, cursor:'pointer', border:'1px solid var(--border)' }}>
                      <span style={{ fontWeight:500, fontSize:'0.85rem' }}>{a}</span>
                      <span style={{ fontSize:'0.7rem', color:PURPLE }}>Playbook →</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Products */}
              {products.length>0 && (
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Products to pitch</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {products.map((p,j)=><span key={j} style={{ fontSize:'0.7rem', padding:'2px 9px', borderRadius:99, background:'#EEEDFE', color:PURPLE, fontWeight:500 }}>{p}</span>)}
                  </div>
                </div>
              )}

              {act.expected_outcome && (
                <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', fontStyle:'italic', borderTop:'1px solid var(--border)', paddingTop:8 }}>
                  Goal: {act.expected_outcome}
                </div>
              )}

              {/* Status + Log visit */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
                <span style={{ fontSize:'0.72rem', padding:'3px 10px', borderRadius:99, fontWeight:600,
                  background:act.status==='done'?'#DCFCE7':act.status==='skipped'?'#FEE2E2':'#FEF3C7',
                  color:act.status==='done'?'#16A34A':act.status==='skipped'?'#DC2626':'#D97706' }}>
                  {act.status==='done'?'✓ Done':act.status==='skipped'?'✗ Skipped':'⏳ Planned'}
                </span>
                {act.status!=='done' && (
                  <button onClick={onLogVisit}
                    style={{ background:PURPLE, color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
                    + Log Visit
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── ACCOUNTS TAB ─────────────────────────────────────────────────
function AccountsTab({ onViewAccount }) {
  const [search, setSearch]   = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (search.length < 2) { setAccounts([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/accounts', { params:{ search, limit:15 } })
        .then(r => setAccounts(r.data.data || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div style={{ padding:16 }}>
      <div style={{ marginBottom:12 }}>
        <input className="form-control" value={search} autoComplete="off"
          placeholder="Search doctor, hospital, distributor…"
          onChange={e=>setSearch(e.target.value)}
          style={{ fontSize:'1rem', padding:'12px 14px', borderRadius:10 }} />
      </div>

      {loading && <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>Searching…</div>}

      {!loading && search.length>=2 && accounts.length===0 && (
        <div style={{ textAlign:'center', padding:30, color:'var(--text-muted)' }}>No accounts found</div>
      )}

      {!loading && search.length<2 && (
        <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🔍</div>
          <div>Type to search accounts</div>
        </div>
      )}

      {accounts.map(a => (
        <div key={a.id} onClick={()=>onViewAccount(a)}
          style={{ background:'var(--bg)', borderRadius:12, marginBottom:10, padding:'12px 14px', border:'1px solid var(--border)', cursor:'pointer' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontWeight:700, fontSize:'0.92rem' }}>{a.name}</div>
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>
                {a.account_type} · {a.speciality||''} · {a.district||''}
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <span style={{ fontSize:'0.68rem', padding:'2px 8px', borderRadius:99, fontWeight:600,
                background:a.priority==='A'?'#EEEDFE':a.priority==='C'?'#FEE2E2':'var(--bg-card)',
                color:a.priority==='A'?PURPLE:a.priority==='C'?'#DC2626':'var(--text-secondary)',
                border:'1px solid var(--border)' }}>
                {a.priority||'B'}
              </span>
              {a.ltv_annual>0 && <div style={{ fontSize:'0.7rem', color:PURPLE, marginTop:3, fontWeight:600 }}>{fmt(a.ltv_annual)}</div>}
            </div>
          </div>
          {a.focus_product_1 && (
            <div style={{ marginTop:8, display:'flex', gap:5 }}>
              <span style={{ fontSize:'0.68rem', padding:'2px 8px', borderRadius:99, background:'#EEEDFE', color:PURPLE }}>{a.focus_product_1}</span>
              {a.focus_product_2 && <span style={{ fontSize:'0.68rem', padding:'2px 8px', borderRadius:99, background:'#EEEDFE', color:PURPLE }}>{a.focus_product_2}</span>}
            </div>
          )}
          <div style={{ marginTop:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:'0.7rem', padding:'1px 7px', borderRadius:99,
              background:`${STAGE_COLOR[a.journey_stage]||'#6B7280'}20`,
              color:STAGE_COLOR[a.journey_stage]||'#6B7280', fontWeight:500 }}>
              {STAGE_LABELS[a.journey_stage]||'Prospect'}
            </span>
            <span style={{ fontSize:'0.72rem', color:PURPLE, fontWeight:500 }}>View playbook →</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── FOLLOW-UPS TAB ───────────────────────────────────────────────
function FollowupsTab({ onLogVisit, onViewAccount }) {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    api.get('/visits', { params:{ to_date:today, limit:50 } })
      .then(r => {
        const overdue = (r.data.data||[]).filter(v=>v.follow_up_date&&new Date(v.follow_up_date)<=new Date());
        setFollowups(overdue);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ padding:16 }}>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Action required</div>
        <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Follow-ups Due</div>
      </div>

      {loading && <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading…</div>}

      {!loading && followups.length===0 && (
        <div style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>✅</div>
          <div style={{ fontWeight:600 }}>All caught up!</div>
          <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginTop:4 }}>No overdue follow-ups</div>
        </div>
      )}

      {followups.map(v => {
        const dueDate = new Date(v.follow_up_date+'T00:00:00');
        const daysOverdue = Math.floor((new Date()-dueDate)/(1000*60*60*24));
        const isToday = v.follow_up_date===today;
        return (
          <div key={v.id} style={{ background:'var(--bg)', borderRadius:12, marginBottom:10, border:`1px solid ${daysOverdue>0?'#FCA5A5':PURPLE}`, overflow:'hidden' }}>
            {/* Urgency banner */}
            <div style={{ padding:'5px 14px', background:daysOverdue>0?'#FEE2E2':isToday?'#FEF3C7':'#EEEDFE',
              fontSize:'0.7rem', fontWeight:600, color:daysOverdue>0?'#DC2626':isToday?'#D97706':PURPLE }}>
              {daysOverdue>0?`${daysOverdue} day${daysOverdue>1?'s':''} overdue`:isToday?'Due today':'Upcoming'}
            </div>
            <div style={{ padding:'12px 14px' }}>
              <div style={{ fontWeight:700, fontSize:'0.92rem', marginBottom:4 }}>
                {v.doctor_name||v.account_name||'—'}
              </div>
              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:8 }}>
                Last visit: {new Date(v.visit_date+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'short'})} · {v.visit_type}
              </div>
              {v.outcome && (
                <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginBottom:10, padding:'6px 10px', background:'var(--bg-card)', borderRadius:6 }}>
                  Last outcome: {v.outcome}
                </div>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={onLogVisit}
                  style={{ flex:1, background:PURPLE, color:'#fff', border:'none', borderRadius:8, padding:'9px', fontSize:'0.8rem', fontWeight:600, cursor:'pointer' }}>
                  + Log Follow-up
                </button>
                <button onClick={()=>onViewAccount({name:v.doctor_name||v.account_name,id:v.doctor_id||v.account_id})}
                  style={{ padding:'9px 14px', background:'none', border:'1px solid var(--border)', borderRadius:8, fontSize:'0.8rem', cursor:'pointer', color:'var(--text-secondary)' }}>
                  Playbook
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── MY STATS TAB ─────────────────────────────────────────────────
function MeTab() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/visits/stats').then(r=>setStats(r.data.data)).catch(()=>{});
  }, []);

  const month = new Date().toLocaleDateString('en-IN',{month:'long',year:'numeric'});

  return (
    <div style={{ padding:16 }}>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{month}</div>
        <div style={{ fontWeight:700, fontSize:'1.1rem' }}>My Performance</div>
      </div>

      {stats && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
            {[
              { l:'Visits MTD',      v:stats.this_month||0,             c:PURPLE },
              { l:'Demos MTD',       v:stats.demos_this_month||0,       c:'#7C3AED' },
              { l:'Unique Accounts', v:stats.unique_doctors_visited||0, c:'#16A34A' },
              { l:'Overdue Follow-ups', v:stats.overdue_followups||0,   c:Number(stats.overdue_followups||0)>0?'#DC2626':'#16A34A' },
            ].map(s=>(
              <div key={s.l} style={{ background:'var(--bg)', borderRadius:12, padding:'14px', textAlign:'center', border:'1px solid var(--border)' }}>
                <div style={{ fontSize:'1.8rem', fontWeight:700, color:s.c }}>{s.v}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:4 }}>{s.l}</div>
              </div>
            ))}
          </div>

          <div style={{ background:'var(--bg)', borderRadius:12, padding:14, border:'1px solid var(--border)' }}>
            <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:10 }}>Quick tips for today</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, fontSize:'0.82rem', color:'var(--text-secondary)' }}>
              {Number(stats.overdue_followups||0)>0 && (
                <div style={{ padding:'8px 12px', background:'#FEE2E2', borderRadius:8, color:'#DC2626', fontWeight:500 }}>
                  {stats.overdue_followups} overdue follow-up{stats.overdue_followups>1?'s':''} — check Follow-ups tab
                </div>
              )}
              <div style={{ padding:'8px 12px', background:'#EEEDFE', borderRadius:8, color:PURPLE }}>
                Log visits immediately after each meeting for best accuracy
              </div>
              <div style={{ padding:'8px 12px', background:'#DCFCE7', borderRadius:8, color:'#16A34A' }}>
                Check Today tab for focus accounts before each visit
              </div>
            </div>
          </div>
        </>
      )}

      <div style={{ marginTop:16, padding:'12px 14px', background:'var(--bg)', borderRadius:12, border:'1px solid var(--border)', textAlign:'center' }}>
        <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginBottom:6 }}>Need full reports and planning?</div>
        <a href="/" style={{ color:PURPLE, fontWeight:600, fontSize:'0.85rem', textDecoration:'none' }}>Open Desktop Version →</a>
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:8 }}>Open on your laptop/desktop for assumptions, reports, travel plan generation and approvals</div>
      </div>
    </div>
  );
}

// ─── ACCOUNT PLAYBOOK MODAL ───────────────────────────────────────
function AccountPlaybookModal({ account: a, onClose, onLogVisit }) {
  const [full, setFull] = useState(a);

  useEffect(() => {
    if (a.id) {
      api.get(`/accounts/${a.id}`)
        .then(r=>setFull(r.data.data||a))
        .catch(()=>{});
    }
  }, [a.id]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:100, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:'16px 16px 0 0', maxHeight:'85vh', overflow:'auto', padding:20 }}>
        {/* Handle */}
        <div style={{ width:40, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 16px' }} />

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'1rem' }}>{full.name}</div>
            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>
              {full.account_type||'Doctor'} · {full.speciality||''} · {full.district||''}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'var(--text-muted)' }}>✕</button>
        </div>

        {/* Playbook cards */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
          {[
            { l:'Priority',     v:full.priority||'B' },
            { l:'LTV',          v:fmt(full.ltv_annual||0) },
            { l:'Journey Stage',v:STAGE_LABELS[full.journey_stage]||'Prospect' },
            { l:'FOT',          v:full.fot_available?'Installed':'Not installed' },
          ].map(f=>(
            <div key={f.l} style={{ background:'var(--bg)', borderRadius:10, padding:'10px 12px' }}>
              <div style={{ fontSize:'0.62rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{f.l}</div>
              <div style={{ fontWeight:700, fontSize:'0.92rem' }}>{f.v}</div>
            </div>
          ))}
        </div>

        {/* Strategy */}
        {(full.focus_product_1||full.growth_play||full.outcome_goal_90d) && (
          <div style={{ background:'#EEEDFE', borderRadius:12, padding:'12px 14px', marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:'0.85rem', color:PURPLE, marginBottom:10 }}>Sales Playbook</div>
            {full.focus_product_1 && (
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:'0.65rem', color:'#403EC9', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>Focus Products</div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                  {[full.focus_product_1,full.focus_product_2].filter(Boolean).map(p=>(
                    <span key={p} style={{ fontSize:'0.75rem', padding:'3px 10px', borderRadius:99, background:'rgba(255,255,255,0.7)', color:PURPLE, fontWeight:600 }}>{p}</span>
                  ))}
                </div>
              </div>
            )}
            {full.growth_play && (
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:'0.65rem', color:'#403EC9', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Growth Play</div>
                <div style={{ fontSize:'0.82rem', color:'#26215C' }}>{full.growth_play}</div>
              </div>
            )}
            {full.outcome_goal_90d && (
              <div>
                <div style={{ fontSize:'0.65rem', color:'#403EC9', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>90-Day Goal</div>
                <div style={{ fontSize:'0.82rem', color:'#26215C' }}>{full.outcome_goal_90d}</div>
              </div>
            )}
          </div>
        )}

        {/* Contact */}
        {(full.contact_number||full.contact) && (
          <a href={`tel:${full.contact_number||full.contact}`}
            style={{ display:'block', padding:'12px', background:'var(--bg)', borderRadius:10, textAlign:'center', color:PURPLE, fontWeight:600, fontSize:'0.9rem', textDecoration:'none', marginBottom:14, border:`1px solid ${PURPLE}` }}>
            📞 Call {full.contact_number||full.contact}
          </a>
        )}

        {/* Log visit button */}
        <button onClick={onLogVisit}
          style={{ width:'100%', background:PURPLE, color:'#fff', border:'none', borderRadius:10, padding:'13px', fontSize:'0.9rem', fontWeight:700, cursor:'pointer' }}>
          + Log Visit for {full.name.split(' ').slice(0,2).join(' ')}
        </button>
      </div>
    </div>
  );
}

// ─── LOG VISIT MODAL (mobile-optimised) ──────────────────────────
function LogVisitModal({ onClose, onSaved }) {
  const [step, setStep]           = useState(1);
  const [search, setSearch]       = useState('');
  const [accounts, setAccounts]   = useState([]);
  const [selAccount, setSelAcc]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({
    visit_date: new Date().toISOString().split('T')[0],
    visit_type:'Follow-up', products_discussed:[],
    demo_done:false, outcome:'', journey_stage_after:'',
    follow_up_date:'', notes:'',
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    if (search.length<2||selAccount) return;
    const t = setTimeout(() => {
      api.get('/accounts',{params:{search,limit:8}}).then(r=>setAccounts(r.data.data||[])).catch(()=>{});
    },300);
    return ()=>clearTimeout(t);
  },[search,selAccount]);

  const toggleProduct = p => setForm(prev=>({...prev,products_discussed:prev.products_discussed.includes(p)?prev.products_discussed.filter(x=>x!==p):[...prev.products_discussed,p]}));

  const save = async () => {
    if(!selAccount)return toast.error('Select an account');
    if(!form.outcome)return toast.error('Select an outcome');
    setSaving(true);
    try {
      // Capture GPS silently — don't block if denied
      let geo = {};
      try {
        const pos = await new Promise((res,rej) =>
          navigator.geolocation?.getCurrentPosition(res, rej, { timeout:8000, maximumAge:30000, enableHighAccuracy:true })
        );
        geo = { geo_lat: parseFloat(pos.coords.latitude.toFixed(7)), geo_lng: parseFloat(pos.coords.longitude.toFixed(7)) };
      } catch { /* GPS unavailable — visit still saves */ }
      await api.post('/visits',{...form,...geo,doctor_id:selAccount.id,account_id:selAccount.id});
      if(form.journey_stage_after) await api.put(`/accounts/${selAccount.id}`,{journey_stage:form.journey_stage_after}).catch(()=>{});
      toast.success('Visit logged!');
      onSaved();
    } catch(e){toast.error(e?.response?.data?.message||'Failed');}
    finally{setSaving(false);}
  };

  const totalSteps = 3;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:'16px 16px 0 0', maxHeight:'92vh', overflow:'auto', display:'flex', flexDirection:'column' }}>
        {/* Handle + header */}
        <div style={{ padding:'12px 16px 0', flexShrink:0 }}>
          <div style={{ width:40, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 14px' }} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:'0.95rem' }}>Log Visit — Step {step}/{totalSteps}</div>
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'var(--text-muted)' }}>✕</button>
          </div>
          {/* Progress bar */}
          <div style={{ height:3, background:'var(--border)', borderRadius:2, marginBottom:16, overflow:'hidden' }}>
            <div style={{ height:'100%', background:PURPLE, borderRadius:2, width:`${(step/totalSteps)*100}%`, transition:'width 0.3s' }} />
          </div>
        </div>

        <div style={{ padding:'0 16px 16px', overflow:'auto', flex:1 }}>

          {/* STEP 1: Account */}
          {step===1 && (
            <div>
              <input className="form-control" value={search} autoFocus
                placeholder="Search doctor, hospital…"
                style={{ fontSize:'1rem', padding:'12px', borderRadius:10, marginBottom:10 }}
                onChange={e=>{setSearch(e.target.value);if(selAccount)setSelAcc(null);}}/>
              {accounts.length>0&&!selAccount&&(
                <div style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', marginBottom:10 }}>
                  {accounts.map(a=>(
                    <div key={a.id} onClick={()=>{setSelAcc(a);setSearch(a.name);setAccounts([]);set('journey_stage_after',a.journey_stage||'');}}
                      style={{ padding:'12px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between' }}>
                      <div>
                        <div style={{ fontWeight:600 }}>{a.name}</div>
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.account_type} · {a.district||''}</div>
                      </div>
                      <span style={{ fontSize:'0.7rem', padding:'2px 8px', borderRadius:99, alignSelf:'center',
                        background:`${STAGE_COLOR[a.journey_stage]||'#6B7280'}20`,
                        color:STAGE_COLOR[a.journey_stage]||'#6B7280', fontWeight:500 }}>
                        {STAGE_LABELS[a.journey_stage]||'Prospect'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {selAccount && (
                <div style={{ background:'#EEEDFE', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
                  <div style={{ fontWeight:700, color:'#26215C' }}>{selAccount.name}</div>
                  <div style={{ fontSize:'0.75rem', color:'#403EC9' }}>{selAccount.account_type} · {selAccount.focus_product_1||''}</div>
                  <div style={{ fontSize:'0.72rem', color:'#403EC9', marginTop:4 }}>{selAccount.growth_play||''}</div>
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                <div>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Visit Date</label>
                  <input type="date" className="form-control" value={form.visit_date} onChange={e=>set('visit_date',e.target.value)}/>
                </div>
                <div>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Visit Type</label>
                  <select className="form-control" value={form.visit_type} onChange={e=>set('visit_type',e.target.value)}>
                    {VISIT_TYPES.map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={()=>setStep(2)} disabled={!selAccount}
                style={{ width:'100%', background:selAccount?PURPLE:'var(--border)', color:selAccount?'#fff':'var(--text-muted)', border:'none', borderRadius:10, padding:13, fontSize:'0.9rem', fontWeight:700, cursor:selAccount?'pointer':'not-allowed' }}>
                Next →
              </button>
            </div>
          )}

          {/* STEP 2: Products + Demo */}
          {step===2 && (
            <div>
              <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:10 }}>Products discussed</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginBottom:16 }}>
                {PRODUCTS.map(p=>(
                  <button key={p} onClick={()=>toggleProduct(p)}
                    style={{ padding:'8px 12px', borderRadius:8, fontSize:'0.78rem', cursor:'pointer', fontWeight:500, border:'none',
                      background:form.products_discussed.includes(p)?PURPLE:'var(--bg)',
                      color:form.products_discussed.includes(p)?'#fff':'var(--text-secondary)' }}>
                    {p}
                  </button>
                ))}
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:10, padding:'12px', background:'var(--bg)', borderRadius:10, marginBottom:16, cursor:'pointer' }}>
                <input type="checkbox" checked={form.demo_done} onChange={e=>set('demo_done',e.target.checked)} style={{ width:20, height:20 }}/>
                <span style={{ fontWeight:600, fontSize:'0.9rem' }}>Demo was conducted</span>
              </label>
              <textarea className="form-control" rows={3} value={form.notes} onChange={e=>set('notes',e.target.value)}
                placeholder="Notes from visit (optional)…" style={{ fontSize:'0.9rem', marginBottom:16 }}/>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={()=>setStep(1)} style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:13, fontSize:'0.88rem', cursor:'pointer' }}>← Back</button>
                <button onClick={()=>setStep(3)} style={{ flex:2, background:PURPLE, color:'#fff', border:'none', borderRadius:10, padding:13, fontSize:'0.9rem', fontWeight:700, cursor:'pointer' }}>Next →</button>
              </div>
            </div>
          )}

          {/* STEP 3: Outcome + Follow-up */}
          {step===3 && (
            <div>
              <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:10 }}>What was the outcome?</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
                {OUTCOMES.map(o=>(
                  <button key={o} onClick={()=>set('outcome',o)}
                    style={{ padding:'11px 14px', borderRadius:9, fontSize:'0.85rem', cursor:'pointer', fontWeight:500, textAlign:'left', border:'none',
                      background:form.outcome===o?PURPLE:'var(--bg)',
                      color:form.outcome===o?'#fff':'var(--text-primary)' }}>
                    {o}
                  </button>
                ))}
              </div>

              <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:8 }}>Update journey stage</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
                {STAGES.map(s=>(
                  <button key={s} onClick={()=>set('journey_stage_after',s)}
                    style={{ padding:'7px 12px', borderRadius:99, fontSize:'0.75rem', cursor:'pointer', fontWeight:500, border:'none',
                      background:form.journey_stage_after===s?STAGE_COLOR[s]:`${STAGE_COLOR[s]}20`,
                      color:form.journey_stage_after===s?'#fff':STAGE_COLOR[s] }}>
                    {STAGE_LABELS[s]}
                  </button>
                ))}
              </div>

              <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:8 }}>Schedule follow-up</div>
              <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                {[7,14,30].map(d=>(
                  <button key={d} onClick={()=>{const dt=new Date();dt.setDate(dt.getDate()+d);set('follow_up_date',dt.toISOString().split('T')[0]);}}
                    style={{ flex:1, padding:'9px', borderRadius:8, fontSize:'0.8rem', border:`1px solid ${form.follow_up_date?PURPLE:'var(--border)'}`, cursor:'pointer', background:'none',
                      color:'var(--text-secondary)', fontWeight:500 }}>
                    +{d} days
                  </button>
                ))}
              </div>
              <input type="date" className="form-control" value={form.follow_up_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e=>set('follow_up_date',e.target.value)} style={{ marginBottom:16 }}/>

              <div style={{ display:'flex', gap:8 }}>
                <button onClick={()=>setStep(2)} style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:13, fontSize:'0.88rem', cursor:'pointer' }}>← Back</button>
                <button onClick={save} disabled={saving||!form.outcome}
                  style={{ flex:2, background:form.outcome?PURPLE:'var(--border)', color:form.outcome?'#fff':'var(--text-muted)', border:'none', borderRadius:10, padding:13, fontSize:'0.9rem', fontWeight:700, cursor:form.outcome?'pointer':'not-allowed' }}>
                  {saving?'Saving…':'Save Visit ✓'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Expenses Tab ─────────────────────────────────────────
function MobileExpensesTab() {
  const [claims, setClaims] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/expenses/claims', { params: { year } }); setClaims(r.data.data || []); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };
  useEffect(()=>{ load(); },[]);

  const createClaim = async (month) => {
    try {
      const r = await api.post('/expenses/claims', { year, month });
      setSel(r.data.data.id);
    } catch(e) { toast.error(e?.response?.data?.message || 'Failed'); }
  };

  if (sel) return <MobileClaimDetail id={sel} onBack={()=>{ setSel(null); load(); }}/>;

  const MONTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const byMonth = {}; claims.forEach(c => byMonth[c.period_month] = c);

  return (
    <div style={{padding:14}}>
      <div style={{marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{fontWeight:700,fontSize:'1.05rem'}}>My Expenses — {year}</div>
      </div>
      {loading ? <div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Loading…</div> :
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {MONTHS.map((m, idx) => {
            const mNum = idx + 1;
            const c = byMonth[mNum];
            const isFuture = mNum > new Date().getMonth() + 1 && year === new Date().getFullYear();
            return (
              <div key={m} onClick={()=> c ? setSel(c.id) : !isFuture && createClaim(mNum)}
                style={{padding:14,borderRadius:10,background:'var(--bg-card)',border:'1px solid var(--border)',cursor:isFuture?'default':'pointer',opacity:isFuture?0.5:1}}>
                <div style={{fontWeight:700,fontSize:'0.95rem'}}>{m}</div>
                {c ? (
                  <>
                    <div style={{fontSize:'1.1rem',fontWeight:700,marginTop:6}}>₹{parseFloat(c.gross_amount||0).toLocaleString('en-IN')}</div>
                    <div style={{fontSize:'0.7rem',color:'var(--text-secondary)',marginTop:2}}>{c.status}</div>
                  </>
                ) : (
                  <div style={{fontSize:'0.78rem',color:'var(--text-muted)',marginTop:6}}>{isFuture ? '—' : '+ Create'}</div>
                )}
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}

function MobileClaimDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try { const r = await api.get(`/expenses/claims/${id}`); setData(r.data.data); }
    catch { toast.error('Failed'); onBack(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!data) return <div style={{padding:40,textAlign:'center'}}>Loading…</div>;

  const { claim, lines, receipts } = data;
  const editable = ['draft','rejected','rep_revoked'].includes(claim.status);

  const autoFill = async () => {
    setBusy(true);
    try { const r = await api.post(`/expenses/claims/${id}/auto-fill`); toast.success(r.data.message); load(); }
    catch(e){ toast.error(e?.response?.data?.message||'Failed'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!window.confirm(`Submit ₹${claim.gross_amount} for approval?`)) return;
    setBusy(true);
    try { await api.post(`/expenses/claims/${id}/submit`); toast.success('Submitted'); load(); }
    catch(e){ toast.error(e?.response?.data?.message||'Failed'); }
    finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5*1024*1024) { toast.error('Max 5 MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('claim_id', id);
      fd.append('source', file.type.startsWith('image/') ? 'mobile_camera' : 'mobile_gallery');
      fd.append('file', file);
      await api.post('/expenses/receipts', fd, { headers:{'Content-Type':'multipart/form-data'} });
      toast.success('Uploaded'); load();
    } catch(err){ toast.error(err?.response?.data?.message||'Failed'); }
    finally { setBusy(false); fileRef.current.value = ''; }
  };

  const MONTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div style={{padding:14,paddingBottom:100}}>
      <button onClick={onBack} style={{background:'none',border:'none',color:PURPLE,fontSize:'0.9rem',marginBottom:10,cursor:'pointer'}}>‹ Back</button>
      <div style={{fontWeight:700,fontSize:'1.1rem'}}>{MONTHS[claim.period_month-1]} {claim.period_year}</div>
      <div style={{fontSize:'0.78rem',color:'var(--text-secondary)',marginTop:2}}>Status: {claim.status}</div>

      <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10,padding:14,marginTop:12}}>
        <div style={{fontSize:'0.72rem',color:'var(--text-secondary)',textTransform:'uppercase'}}>Gross</div>
        <div style={{fontSize:'1.5rem',fontWeight:700}}>₹{parseFloat(claim.gross_amount||0).toLocaleString('en-IN')}</div>
        {parseFloat(claim.advance_adjusted)>0 && <div style={{fontSize:'0.78rem',color:'var(--text-secondary)',marginTop:4}}>Net payable: ₹{parseFloat(claim.net_payable).toLocaleString('en-IN')}</div>}
      </div>

      {editable && <div style={{display:'flex',gap:8,marginTop:12,flexWrap:'wrap'}}>
        <button disabled={busy} onClick={autoFill} style={{flex:1,padding:'12px',borderRadius:8,background:PURPLE,color:'#fff',border:'none',fontWeight:600,cursor:'pointer'}}>⚡ Auto-fill GPS</button>
        {lines.length > 0 && <button disabled={busy} onClick={submit} style={{flex:1,padding:'12px',borderRadius:8,background:'#16A34A',color:'#fff',border:'none',fontWeight:600,cursor:'pointer'}}>Submit</button>}
      </div>}

      <div style={{marginTop:16}}>
        <div style={{fontWeight:600,marginBottom:8}}>Lines ({lines.length})</div>
        {lines.map(l => (
          <div key={l.id} style={{padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:'0.82rem',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.description}</div>
              <div style={{fontSize:'0.7rem',color:'var(--text-muted)',marginTop:2}}>{l.expense_date?.split('T')[0]} · {l.category}{l.subcategory?`/${l.subcategory}`:''}</div>
            </div>
            <div style={{fontWeight:700,fontSize:'0.92rem',whiteSpace:'nowrap'}}>₹{parseFloat(l.amount).toLocaleString('en-IN')}</div>
          </div>
        ))}
        {!lines.length && <div style={{textAlign:'center',color:'var(--text-muted)',padding:20,fontSize:'0.82rem'}}>No lines yet</div>}
      </div>

      <div style={{marginTop:16}}>
        <div style={{display:'flex',alignItems:'center',marginBottom:8}}>
          <div style={{fontWeight:600}}>Receipts ({receipts.length})</div>
          <div style={{flex:1}}/>
          {editable && <>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" style={{display:'none'}} onChange={upload}/>
            <button disabled={busy} onClick={()=>fileRef.current.click()} style={{padding:'8px 14px',borderRadius:8,background:PURPLE,color:'#fff',border:'none',fontWeight:500,fontSize:'0.82rem',cursor:'pointer'}}>📷 Add</button>
          </>}
        </div>
        {receipts.map(r => (
          <a key={r.id} href={`/api/v1/expenses/receipts/${r.id}/file`} target="_blank" rel="noreferrer"
             style={{display:'flex',alignItems:'center',gap:8,padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,marginBottom:6,textDecoration:'none',color:'var(--text-primary)'}}>
            <span style={{fontSize:'1.3rem'}}>{r.file_type?.includes('pdf')?'📄':'📷'}</span>
            <div style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:'0.82rem'}}>{r.original_name}</div>
            <span style={{fontSize:'0.7rem',color:'var(--text-muted)'}}>{(r.file_size/1024).toFixed(0)} KB</span>
          </a>
        ))}
        {!receipts.length && <div style={{textAlign:'center',color:'var(--text-muted)',padding:20,fontSize:'0.82rem'}}>No receipts yet</div>}
      </div>
    </div>
  );
}
